/**
 * Lottery Renderer — slot machine weapon selection visuals.
 *
 * Four slots float above the active tank in a crown formation.
 * Each slot scrolls through weapon names vertically, then locks
 * to reveal the final weapon with rarity-colored effects.
 * Player presses 1/2/3/4 to pick; unchosen slots fade out.
 * Slot 4 is always Mortar (safe fallback).
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { LOTTERY, Z, WORLD } from '../constants';
import type { WeaponDef, Rarity } from '../weapons';

const SLOT_W = 120;
const SLOT_H = 44;
const SLOT_GAP = 16;
const SLOT_TOTAL_W = SLOT_W * 4 + SLOT_GAP * 3;
const REEL_SPEED_INITIAL = 800; // px/sec vertical scroll
const LOCK_BOUNCE_PX = 6;       // overshoot on lock snap
const PICK_FADE_SPEED = 4.0;    // alpha/sec for unchosen slot fade

function rarityColor(rarity: Rarity): number {
  switch (rarity) {
    case 'common': return LOTTERY.COLOR_COMMON;
    case 'uncommon': return LOTTERY.COLOR_UNCOMMON;
    case 'rare': return LOTTERY.COLOR_RARE;
    case 'epic': return LOTTERY.COLOR_EPIC;
    case 'legendary': return LOTTERY.COLOR_LEGENDARY;
  }
}

interface SlotState {
  container: Container;
  bg: Graphics;
  nameText: Text;
  labelText: Text;      // "1" / "2" / "3" label
  locked: boolean;
  weapon: WeaponDef | null;
  scrollOffset: number;  // vertical scroll px
  scrollSpeed: number;   // current speed (decelerating)
  lockBounce: number;    // bounce animation offset
  lockFlash: number;     // flash intensity (0..1)
  fadeAlpha: number;     // 1.0 = visible, fading to 0
  picked: boolean;
}

export class LotteryRenderer {
  private worldContainer: Container;
  private uiContainer: Container;
  private lotteryContainer: Container;
  private slots: SlotState[] = [];
  private active: boolean = false;
  private pickMode: boolean = false;
  private followX: number = 0;
  private followY: number = 0;

  // Timer bar (screen-space)
  private timerBarContainer: Container;
  private timerBarBg: Graphics;
  private timerBarFill: Graphics;

  constructor(worldContainer: Container, uiContainer: Container) {
    this.worldContainer = worldContainer;
    this.uiContainer = uiContainer;

    // World-space container for lottery slots
    this.lotteryContainer = new Container();
    this.lotteryContainer.zIndex = Z.DI_OVERLAY + 1; // above DI
    this.lotteryContainer.visible = false;
    this.worldContainer.addChild(this.lotteryContainer);

    // Timer bar (screen-space) — shows pick timeout
    this.timerBarContainer = new Container();
    this.timerBarContainer.visible = false;
    this.timerBarContainer.zIndex = Z.FLASH;
    this.uiContainer.addChild(this.timerBarContainer);

    this.timerBarBg = new Graphics();
    this.timerBarBg.roundRect(WORLD.CANVAS_WIDTH / 2 - 100, WORLD.CANVAS_HEIGHT - 60, 200, 8, 4);
    this.timerBarBg.fill({ color: 0x222233, alpha: 0.7 });
    this.timerBarContainer.addChild(this.timerBarBg);

    this.timerBarFill = new Graphics();
    this.timerBarContainer.addChild(this.timerBarFill);

    // Create 4 slot containers (3 random + mortar)
    const nameStyle = new TextStyle({
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: 16,
      fill: 0xffffff,
      align: 'center',
      stroke: { color: 0x000000, width: 3 },
    });
    const labelStyle = new TextStyle({
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: 14,
      fill: 0xffffff,
      align: 'center',
      stroke: { color: 0x000000, width: 3 },
    });

    for (let i = 0; i < 4; i++) {
      const container = new Container();
      container.visible = false;

      const bg = new Graphics();
      container.addChild(bg);

      const nameText = new Text({ text: '', style: nameStyle });
      nameText.anchor.set(0.5, 0.5);
      container.addChild(nameText);

      const labelText = new Text({ text: `${i + 1}`, style: labelStyle });
      labelText.anchor.set(0.5, 0);
      labelText.visible = false;
      container.addChild(labelText);

      this.lotteryContainer.addChild(container);
      this.slots.push({
        container,
        bg,
        nameText,
        labelText,
        locked: false,
        weapon: null,
        scrollOffset: 0,
        scrollSpeed: REEL_SPEED_INITIAL,
        lockBounce: 0,
        lockFlash: 0,
        fadeAlpha: 1.0,
        picked: false,
      });
    }
  }

  /** Show lottery with 4 weapon slots above the given player. */
  show(playerIndex: number, playerX: number, playerY: number, slots: WeaponDef[]): void {
    this.active = true;
    this.pickMode = false;
    this.followX = playerX;
    this.followY = playerY;
    this.lotteryContainer.visible = true;

    for (let i = 0; i < 4; i++) {
      const s = this.slots[i];
      s.weapon = slots[i] ?? null;
      s.locked = false;
      s.scrollOffset = 0;
      s.scrollSpeed = REEL_SPEED_INITIAL * (0.8 + i * 0.12); // slightly different speeds
      s.lockBounce = 0;
      s.lockFlash = 0;
      s.fadeAlpha = 1.0;
      s.picked = false;
      s.container.visible = true;
      s.labelText.visible = false;
      s.nameText.text = '';
    }
  }

  /** Lock a specific slot to its final weapon. */
  lock(slotIndex: number): void {
    if (slotIndex < 0 || slotIndex >= 4) return;
    const s = this.slots[slotIndex];
    s.locked = true;
    s.scrollSpeed = 0;
    s.lockBounce = LOCK_BOUNCE_PX;
    s.lockFlash = 1.0;
    if (s.weapon) {
      s.nameText.text = s.weapon.name.toUpperCase();
    }
  }

  /** Enter pick mode — show 1/2/3 labels. */
  enterPick(): void {
    this.pickMode = true;
    this.timerBarContainer.visible = true;
    for (const s of this.slots) {
      s.labelText.visible = true;
    }
  }

  /** Player picked a slot — highlight it, fade others. */
  pick(slotIndex: number): void {
    for (let i = 0; i < 4; i++) {
      this.slots[i].picked = i === slotIndex;
    }
  }

  /** Hide everything. */
  hide(): void {
    this.active = false;
    this.pickMode = false;
    this.lotteryContainer.visible = false;
    this.timerBarContainer.visible = false;
    for (const s of this.slots) {
      s.container.visible = false;
    }
  }

  /** Update visuals — called every render frame. */
  update(state: import('../types').GameState, dt: number): void {
    if (!this.active) return;

    // Track player position for slot placement
    const player = state.players[state.currentPlayer];
    this.followX = player.x;
    this.followY = player.y;

    // Fake weapon names for reel scroll effect
    const fakeNames = ['MORTAR', 'DIGGER', 'BOUNCER', 'SEEKER', 'DRILL', 'NUKE', 'AIRBURST', '???'];

    for (let i = 0; i < 4; i++) {
      const s = this.slots[i];

      // Position slot above player — 4 slots centered
      const slotX = this.followX + (i - 1.5) * (SLOT_W + SLOT_GAP);
      const slotY = this.followY + LOTTERY.SLOT_CENTER_Y + (i === 1 || i === 2 ? 0 : 10);

      s.container.x = slotX;
      s.container.y = slotY + s.lockBounce;

      // Reel scroll animation (before lock)
      if (!s.locked) {
        s.scrollOffset += s.scrollSpeed * dt;
        // Cycle through fake names
        const nameIdx = Math.floor(s.scrollOffset / 30) % fakeNames.length;
        s.nameText.text = fakeNames[nameIdx];
      }

      // Lock bounce decay
      if (s.lockBounce > 0.1) {
        s.lockBounce *= Math.pow(0.05, dt); // rapid spring decay
      } else {
        s.lockBounce = 0;
      }

      // Lock flash decay
      if (s.lockFlash > 0) {
        s.lockFlash -= dt * 3;
        if (s.lockFlash < 0) s.lockFlash = 0;
      }

      // Fade after pick — unchosen fade fast, picked slot fades slower after unchosen are gone
      if (this.pickMode && this.slots.some(sl => sl.picked)) {
        if (!s.picked) {
          s.fadeAlpha -= PICK_FADE_SPEED * dt;
          if (s.fadeAlpha < 0) s.fadeAlpha = 0;
        } else {
          // Picked slot: start fading after unchosen are gone
          const unchosenGone = this.slots.every(sl => sl.picked || sl.fadeAlpha <= 0);
          if (unchosenGone) {
            s.fadeAlpha -= PICK_FADE_SPEED * 0.5 * dt;
            if (s.fadeAlpha < 0) s.fadeAlpha = 0;
          }
        }
      }

      // Draw slot background
      s.bg.clear();
      const rarity = s.weapon?.rarity ?? 'common';
      const color = s.locked ? rarityColor(rarity) : 0x444466;
      const borderAlpha = s.locked ? 0.9 + s.lockFlash * 0.1 : 0.5;

      s.bg.roundRect(-SLOT_W / 2, -SLOT_H / 2, SLOT_W, SLOT_H, 6);
      s.bg.fill({ color: 0x0a0a19, alpha: 0.85 * s.fadeAlpha });
      s.bg.roundRect(-SLOT_W / 2, -SLOT_H / 2, SLOT_W, SLOT_H, 6);
      s.bg.stroke({ color, alpha: borderAlpha * s.fadeAlpha, width: s.locked ? 2.5 : 1.5 });

      // Lock flash glow
      if (s.lockFlash > 0.01) {
        s.bg.roundRect(-SLOT_W / 2 - 3, -SLOT_H / 2 - 3, SLOT_W + 6, SLOT_H + 6, 8);
        s.bg.fill({ color, alpha: s.lockFlash * 0.25 * s.fadeAlpha });
      }

      // Picked highlight
      if (s.picked) {
        s.bg.roundRect(-SLOT_W / 2 - 2, -SLOT_H / 2 - 2, SLOT_W + 4, SLOT_H + 4, 8);
        s.bg.stroke({ color: 0xffffff, alpha: 0.8, width: 2 });
      }

      // Text
      s.nameText.x = 0;
      s.nameText.y = 0;
      s.nameText.alpha = s.fadeAlpha;
      if (s.locked && s.weapon) {
        s.nameText.style.fill = rarityColor(s.weapon.rarity);
      } else {
        s.nameText.style.fill = 0x888899;
      }

      // Label position
      s.labelText.x = 0;
      s.labelText.y = SLOT_H / 2 + 4;
      s.labelText.alpha = s.fadeAlpha * 0.7;
    }

    // Timer bar during pick phase
    if (this.pickMode && state.phase === 'lottery_pick') {
      const t = Math.max(0, state.lotteryPickTimeout / LOTTERY.PICK_TIMEOUT);
      this.timerBarFill.clear();
      const barW = 200 * t;
      const barColor = t > 0.3 ? 0x44ff88 : 0xff4444;
      this.timerBarFill.roundRect(WORLD.CANVAS_WIDTH / 2 - 100, WORLD.CANVAS_HEIGHT - 60, barW, 8, 4);
      this.timerBarFill.fill({ color: barColor, alpha: 0.9 });
    } else {
      // Hide timer bar outside lottery_pick (covers transition to aiming)
      this.timerBarContainer.visible = false;
      this.timerBarFill.clear();
    }

    // Auto-hide after all slots have faded out (pick animation complete)
    if (this.slots.some(sl => sl.picked) && this.slots.every(sl => sl.fadeAlpha <= 0)) {
      this.hide();
    }
  }

  destroy(): void {
    this.lotteryContainer.destroy({ children: true });
    this.timerBarContainer.destroy({ children: true });
  }
}
